
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, X, User, Star, FileText, Table2, Gauge, BarChart3 } from 'lucide-react';
import './StatGenieReportCardV2.css';
import {
  LIBRARY_CATEGORIES,
  PAIRED_TABLES,
  isLegacyTable,
  resolveLegacyRemap,
  getCanonicalLabel,
  getTimeHistoryPairing,
} from '../utils/canonicalTables';
import SkillMeter from './SkillMeter';
import DataExplorer from './DataExplorer';

const CATEGORY_OPTIONS = [
  { value: 'General', label: 'General (UR)' },
  { value: 'NC-OBC', label: 'NC-OBC' },
  { value: 'EWS', label: 'EWS' },
  { value: 'SC', label: 'SC' },
  { value: 'ST', label: 'ST' },
  { value: 'PWD', label: 'PWD (Persons with Disabilities)' },
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// -------------------------------------------------------------------------
// Beta status components
// Why colocated? They are tiny, V2-only, and reviewing them in context keeps
// the header-area markup readable. Promote to a shared module only if reused.
// -------------------------------------------------------------------------
const BETA_TOOLTIP = 'This is the BETA version of the AI-generated insights — please share feedback in the rating section below.';

const BetaBadge = () => (
  <span
    className="sg-beta-badge"
    title={BETA_TOOLTIP}
    aria-label="Beta version"
  >
    BETA
  </span>
);

// Banner is dismissible via ×. Local state only — reappears on every report
// load because accuracy/clarity may materially change between reports, so
// the notice should remain honest on each view.
const BetaInfoBanner = () => {
  const [visible, setVisible] = useState(true);
  if (!visible) return null;
  return (
    <div className="sg-beta-banner" role="status">
      <span className="sg-beta-banner__icon" aria-hidden="true">🧪</span>
      <div className="sg-beta-banner__body">
        <p className="sg-beta-banner__text">
          <span className="sg-beta-banner__text--desktop">
            This is the BETA version of the AI-generated insights — final update for this SIMCAT season. We'd love your feedback on which parts you found most useful and anything that could be clearer. Use the rating section below.
          </span>
          <span className="sg-beta-banner__text--mobile">
            BETA — final update this SIMCAT season. Share feedback in the rating section below.
          </span>
        </p>
        <div className="sg-beta-banner__whats-new">
          <strong className="sg-beta-banner__whats-new-title">
            <span className="sg-beta-banner__text--desktop">What's new:</span>
            <span className="sg-beta-banner__text--mobile">What's new:</span>
          </strong>
          <ul className="sg-beta-banner__whats-new-list">
            <li>
              <span className="sg-beta-banner__text--desktop"><strong>Skill Meter</strong> — a visual strength map across sections, areas, and topics so you can see where you stand at a glance.</span>
              <span className="sg-beta-banner__text--mobile"><strong>Skill Meter</strong> — visual strength map across sections, areas &amp; topics.</span>
            </li>
            <li>
              <span className="sg-beta-banner__text--desktop"><strong>Data Explorer</strong> — build your own charts: pick a measure, slice by section/area/topic, and compare across tests.</span>
              <span className="sg-beta-banner__text--mobile"><strong>Data Explorer</strong> — build your own charts, compare across tests.</span>
            </li>
          </ul>
        </div>
      </div>
      <button
        type="button"
        className="sg-beta-banner__close"
        onClick={() => setVisible(false)}
        aria-label="Dismiss beta notice"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

const readinessTone = (gap, status) => {
  if (typeof gap !== 'number' || !status) return 'Readiness unavailable';
  if (status === 'CLEARED') return `${Math.abs(gap)} marks above cutoff`;
  if (status === 'MISSED') return `${Math.abs(gap)} marks short`;
  return 'Readiness unavailable';
};

const statusPillClass = (status) => {
  if (status === 'CLEARED') return 'sg-pill sg-pill--success';
  if (status === 'MISSED') return 'sg-pill sg-pill--danger';
  return 'sg-pill sg-pill--neutral';
};

const formatPercentile = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toFixed(2).replace(/\.00$/, '')}%ile`;
};

const mkReadiness = (score, tierCutoff, percentile) => ({
  score,
  cutoff: tierCutoff,
  gap: typeof score === 'number' && typeof tierCutoff === 'number' ? score - tierCutoff : null,
  status:
    typeof score === 'number' && typeof tierCutoff === 'number'
      ? score >= tierCutoff
        ? 'CLEARED'
        : 'MISSED'
      : 'N/A',
  percentile
});

const buildTopCards = (detailedData) => {
  const overall = detailedData?.current_performance?.overall;
  const sections = detailedData?.current_performance?.sections || {};

  const cards = [];
  if (overall) {
    cards.push({
      key: 'overall',
      title: 'Overall',
      marks: overall.score,
      percentile: overall.percentile,
      other: mkReadiness(overall.score, overall.non_blacki_cutoff, overall.percentile),
      blacki: mkReadiness(overall.score, overall.blacki_cutoff, overall.percentile)
    });
  }

  const mapping = [
    ['varc', 'VARC'],
    ['dilr', 'DILR'],
    ['qa', 'QA']
  ];

  mapping.forEach(([key, title]) => {
    const s = sections[key];
    if (!s) return;
    cards.push({
      key,
      title,
      marks: s.score,
      percentile: s.percentile,
      other: mkReadiness(s.score, s.non_blacki_cutoff, s.percentile),
      blacki: mkReadiness(s.score, s.blacki_cutoff, s.percentile),
      // Add Type A/B/C breakdown
      type_a: s.type_a,
      type_b: s.type_b,
      type_c: s.type_c
    });
  });

  return cards;
};

const parseInsights = (html) => {
  if (!html) return { summary: [], sections: [], priorities: [] };

  const cleaned = html
    .replace(/<\/think>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();

  const lines = cleaned
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)
    // Don't filter out ## 2. and ## 3. - they're valid v4 headers
    .filter((line) => !/^##\s*1\.\s*UNDERSTANDING/i.test(line));

  const summary = [];
  const sections = [];
  const priorities = [];

  let currentSection = null;
  let inAction = false;

  for (const line of lines) {
    if (/^YOUR DIAGNOSIS/i.test(line) || /^GROWTH TRAJECTORY/i.test(line)) continue;

    if (/^SECTION-BY-SECTION/i.test(line)) {
      currentSection = null;
      inAction = false;
      continue;
    }

    if (/^YOUR ACTION PLAN/i.test(line) || /^NEXT 2 TESTS PLAN/i.test(line)) {
      inAction = true;
      currentSection = null;
      continue;
    }

    if (/^#{1,4}\s*VARC/i.test(line) || /^VARC Analysis/i.test(line) || /###\s*VARC Analysis/i.test(line)) {
      // Check if VARC section already exists to avoid duplicates
      const existing = sections.find(s => s.title === 'VARC');
      if (existing) {
        currentSection = existing;
      } else {
        currentSection = { title: 'VARC', findings: [] };
        sections.push(currentSection);
      }
      continue;
    }

    if (/^#{1,4}\s*DILR/i.test(line) || /^DILR Analysis/i.test(line) || /###\s*DILR Analysis/i.test(line)) {
      const existing = sections.find(s => s.title === 'DILR');
      if (existing) {
        currentSection = existing;
      } else {
        currentSection = { title: 'DILR', findings: [] };
        sections.push(currentSection);
      }
      continue;
    }

    if (/^#{1,4}\s*QA/i.test(line) || /^QA Analysis/i.test(line) || /###\s*QA Analysis/i.test(line)) {
      const existing = sections.find(s => s.title === 'QA');
      if (existing) {
        currentSection = existing;
      } else {
        currentSection = { title: 'QA', findings: [] };
        sections.push(currentSection);
      }
      continue;
    }

    if (inAction) {
      priorities.push(line);
      continue;
    }

    if (currentSection) {
      // Check if this line is a new finding header (starts with ** or #)
      // or if it should be appended to the previous finding
      const isNewFindingHeader = /^\*\*[^*]+\*\*\s*$/.test(line) || /^#{1,4}\s+/.test(line);
      const isBulletPoint = /^[-*]\s+/.test(line);
      
      if (isNewFindingHeader) {
        // Start a new finding
        currentSection.findings.push(line);
      } else if (isBulletPoint && currentSection.findings.length > 0) {
        // Append to the last finding (bullet point continuation)
        const lastIdx = currentSection.findings.length - 1;
        currentSection.findings[lastIdx] += '\n' + line;
      } else if (currentSection.findings.length > 0) {
        // Append to the last finding (regular text continuation)
        const lastIdx = currentSection.findings.length - 1;
        currentSection.findings[lastIdx] += '\n' + line;
      } else {
        // First finding in this section
        currentSection.findings.push(line);
      }
    } else {
      summary.push(line);
    }
  }

  return { summary, sections, priorities };
};

const processMarkdown = (text) => {
  if (!text) return '';
  let html = text;
  // Convert newlines to <br> tags for proper rendering
  html = html.replace(/\n/g, '<br>');
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/\[\[TABLE:([^|]+)\|([^\]]+)\]\]/g, '<span class="sg-evidence-ref">$2</span>');
  html = html.replace(/⚠️/g, '<span class="sg-icon sg-icon--warning">⚠️</span>');
  html = html.replace(/✅/g, '<span class="sg-icon sg-icon--success">✅</span>');
  html = html.replace(/❌/g, '<span class="sg-icon sg-icon--danger">❌</span>');
  return html;
};

const findingEvidenceMap = {
  // Standardized 3-finding structure across all sections
  // Each finding maps to a SINGLE primary evidence table
  // Finding 1: Question Selection Strategy -> Section Snapshot
  // Finding 2: Time Management -> Time Analysis
  // Finding 3: Skill Meter -> Topic Insights
  
  VARC: [
    { match: /selection|type a|type b|attempt|easy|medium/i, tableId: 'varc_section_snapshot' },
    { match: /time|waste|speed|allocation/i, tableId: 'varc_time_analysis' },
    { match: /topic|area|verbal|rc|va|strength|weak/i, tableId: 'varc_topic_insights' }
  ],
  DILR: [
    { match: /selection|type a|type b|attempt|easy|medium/i, tableId: 'dilr_section_snapshot' },
    { match: /time|waste|speed|allocation/i, tableId: 'dilr_time_analysis' },
    { match: /topic|area|di|lr|set|strength|weak/i, tableId: 'dilr_topic_insights' }
  ],
  QA: [
    { match: /selection|type a|type b|attempt|easy|medium/i, tableId: 'qa_section_snapshot' },
    { match: /time|waste|speed|allocation/i, tableId: 'qa_time_analysis' },
    { match: /topic|area|arithmetic|algebra|geometry|strength|weak/i, tableId: 'qa_topic_insights' }
  ]
};

const getFindingRule = (sectionTitle, finding) => {
  const rules = findingEvidenceMap[sectionTitle] || [];
  return rules.find((r) => r.match.test(finding)) || null;
};

const useIsMobile = (breakpoint = 860) => {
  const getValue = () => (typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false);
  const [isMobile, setIsMobile] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsMobile(getValue());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);

  return isMobile;
};

const CompactTopCard = ({ card }) => (
  <div className={`sg-top-card ${card.key === 'overall' ? 'sg-top-card--overall' : ''}`}>
    <div className="sg-top-card__title">{card.title}</div>
    <div className="sg-top-card__percentile">{formatPercentile(card.percentile)}</div>
    <div className="sg-top-card__marks">{card.marks ?? '—'} marks</div>
    <div className="sg-iim-readiness-label">IIM Readiness</div>
    <div className="sg-top-card__readiness">
      <div className="sg-readiness-row">
        <span className="sg-readiness-row__label">Other IIMs</span>
        <span className={statusPillClass(card.other?.status)}>{readinessTone(card.other?.gap, card.other?.status)}</span>
      </div>
      <div className="sg-readiness-row">
        <span className="sg-readiness-row__label">Old IIMs (ABCLKI)</span>
        <span className={statusPillClass(card.blacki?.status)}>{readinessTone(card.blacki?.gap, card.blacki?.status)}</span>
      </div>
    </div>
    {/* Type A/B/C Breakdown */}
    {card.type_a && (
      <div className="sg-type-breakdown">
        <div className="sg-type-row">
          <span className="sg-type-label">Type A:</span>
          <span className="sg-type-value">{card.type_a.attempted}/{card.type_a.available} ({card.type_a.accuracy}%)</span>
        </div>
        <div className="sg-type-row">
          <span className="sg-type-label">Type B:</span>
          <span className="sg-type-value">{card.type_b.attempted}/{card.type_b.available} ({card.type_b.accuracy}%)</span>
        </div>
        <div className="sg-type-row">
          <span className="sg-type-label">Type C:</span>
          <span className="sg-type-value">{card.type_c.attempted}/{card.type_c.available} ({card.type_c.accuracy}%)</span>
        </div>
      </div>
    )}
  </div>
);

// TierPerformanceSummary removed - top cards already show factual scores

const deriveCurrentInterpretation = (topCards, lens) => {
  const tierKey = lens === 'BLACKI' ? 'blacki' : 'other';
  const tierLabel = lens === 'BLACKI' ? 'Old IIMs (ABCLKI)' : 'Other IIMs';
  const overall = topCards.find((c) => c.key === 'overall');
  const sections = topCards.filter((c) => c.key !== 'overall');

  // v7.4: Enrich with actual mark deltas so off-tier prose carries diagnostic value
  // beyond “missed/cleared” labels. Why: the AI now writes prose only for the active tier;
  // the off-tier toggle falls back here, and a number-rich one-liner gives the user a
  // tangible distance reading without needing the AI to restate it.
  const fmtGap = (gap) => {
    if (typeof gap !== 'number') return '';
    return gap >= 0 ? `+${gap}` : `${gap}`; // negative sign already present
  };
  const labelFor = (sec) => {
    const g = sec[tierKey]?.gap;
    return typeof g === 'number' ? `${sec.title} (${fmtGap(g)})` : sec.title;
  };

  const missed = sections.filter((s) => s[tierKey]?.status === 'MISSED');
  const cleared = sections.filter((s) => s[tierKey]?.status === 'CLEARED');
  const overallGap = overall?.[tierKey]?.gap;
  const overallSuffix = typeof overallGap === 'number'
    ? ` (overall ${fmtGap(overallGap)} marks)`
    : '';

  if (overall?.[tierKey]?.status === 'CLEARED' && missed.length === 0) {
    return `Your profile is already safe against ${tierLabel} benchmarks${overallSuffix}.`;
  }

  if (missed.length === 0 && cleared.length > 0) {
    return `${cleared.map(labelFor).join(' and ')} ${cleared.length > 1 ? 'are' : 'is'} above the ${tierLabel} cutoff, but you need more overall marks${overallSuffix} to clear the threshold.`;
  }

  if (cleared.length === 0) {
    return `All three sections are short of ${tierLabel} cutoffs — ${missed.map(labelFor).join(', ')}.`;
  }

  return `${cleared.map(labelFor).join(' and ')} ${cleared.length > 1 ? 'are' : 'is'} above the ${tierLabel} cutoff, but ${missed.map(labelFor).join(' and ')} ${missed.length > 1 ? 'are' : 'is'} keeping you short of ${tierLabel} readiness.`;
};

const deriveHistoricalPattern = (summary, lens) => {
  const tierLabel = lens === 'BLACKI' ? 'Old IIMs (ABCLKI)' : 'Other IIMs';
  
  // Look for AI-generated historical pattern in summary
  const patternLine = summary.find((line) => 
    line.length > 20 && 
    line.length < 150 && 
    !line.includes('CURRENT') && 
    !line.includes('POSITION') &&
    !line.match(/^(Overall|VARC|DILR|QA):/i) &&
    (line.includes('test') || line.includes('recent') || line.includes('pattern') || line.includes('history') || line.includes('trend'))
  );
  
  if (patternLine) {
    // Clean up any leaked prompt headings
    return patternLine
      .replace(/\*\*HISTORICAL PATTERN\*\*/gi, '')
      .replace(/\*\*HISTORICAL\*\*/gi, '')
      .replace(/\*\*PATTERN\*\*/gi, '')
      .replace(/^[-:]+\s*/, '')
      .trim();
  }
  
  return `Check the historical trend to see if this ${tierLabel} performance is repeating or improving.`;
};

const deriveImmediateGoal = (topCards, category) => {
  const overall = topCards.find((c) => c.key === 'overall');
  const sections = topCards.filter((c) => c.key !== 'overall');

  const missedOther = sections.filter((s) => s.other?.status === 'MISSED');
  const missedBlacki = sections.filter((s) => s.blacki?.status === 'MISSED');

  if (missedOther.length > 0) {
    const closest = missedOther.reduce((prev, curr) =>
      Math.abs(curr.other?.gap ?? 999) < Math.abs(prev.other?.gap ?? 999) ? curr : prev
    );
    return {
      title: 'Raise your overall score into the safe zone for Other IIMs',
      why: `Do not overthink Old IIMs (ABCLKI) right now. First stop sectional leakage, starting with ${closest.title}. Once you clear Other IIM benchmarks more consistently, Old IIMs (ABCLKI) chances also improve.`,
      focus: `Immediate focus: ${closest.title} is the closest section to fix for the first safe benchmark.`
    };
  }

  if (overall?.other?.status === 'MISSED') {
    const stable = sections.filter((s) => s.other?.status === 'CLEARED');
    const focusSection = stable[0]?.title || 'your strongest sections';
    return {
      title: 'Raise your overall score into the safe zone for Other IIMs',
      why: `You have already built the sectional base for Other IIMs. Now use the most convertible sections — especially ${focusSection} — to raise total marks. This also improves Old IIMs (ABCLKI) chances, but your immediate goal remains one: become safe first.`,
      focus: `Immediate focus: use your most stable sections to add overall marks without creating new sectional leaks.`
    };
  }

  if (missedBlacki.length > 0) {
    const closest = missedBlacki.reduce((prev, curr) =>
      Math.abs(curr.blacki?.gap ?? 999) < Math.abs(prev.blacki?.gap ?? 999) ? curr : prev
    );
    return {
      title: 'Move from safe to Old IIMs (ABCLKI)-ready',
      why: `You have already secured the first benchmark. The next step is to strengthen the sections that still keep you short of Old IIMs (ABCLKI) — starting with ${closest.title}.`,
      focus: `Immediate focus: build the next layer without letting the safe base slip.`
    };
  }

  return {
    title: 'Maximize percentile from a stable base',
    why: 'The visible cutoffs are no longer the main issue. Now the job is to convert more medium opportunities without disturbing your strongest processes.',
    focus: 'Immediate focus: maintain selection discipline and push percentile selectively.'
  };
};

// QUICK WIN: Derive immediate goal from progression object (coach-routed, not card-routed)
const deriveImmediateGoalFromProgression = (progression) => {
  if (!progression) {
    return {
      title: 'Focus on clearing sectional cutoffs',
      why: 'Building a strong foundation across all sections is the first step toward a competitive score.',
      focus: 'Immediate focus: ensure you can consistently clear cutoffs in all three sections.'
    };
  }

  const { active_context, current_milestone, stage_label } = progression;
  const activeTier = active_context?.active_tier || 'NON_BLACKI';
  const tierLabel = activeTier === 'BLACKI' ? 'Old IIMs (ABCLKI)' : 'Other IIMs';
  const blockerType = active_context?.blocker_type;
  const prioritySection = current_milestone?.priority_section;
  const milestoneType = current_milestone?.type; // 'cutoff_clearance' or 'maximize'

  // Case 1: Still clearing sectionals (cutoff_clearance milestone)
  if (milestoneType === 'cutoff_clearance' || blockerType === 'sectional') {
    const sectionText = prioritySection ? `${prioritySection} first` : 'sectional cutoffs first';
    return {
      title: `Clear ${tierLabel} sectional cutoffs first`,
      why: `You are still missing sectional benchmarks. Without clearing all three sectionals, your overall percentile remains irrelevant for admissions. Fix ${sectionText}.`,
      focus: `Immediate focus: ${prioritySection || 'your weakest section'} is the priority blocker to address.`
    };
  }

  // Case 2: Sectionals cleared, need overall score
  if (milestoneType === 'overall_score_build') {
    return {
      title: `Raise your overall score into the safe zone for ${tierLabel}`,
      why: `You have built the sectional base. Now use your most stable sections to raise total marks without creating new sectional leaks.`,
      focus: 'Immediate focus: add overall marks selectively while protecting your cleared sectionals.'
    };
  }

  // Case 3: Safe for Other IIMs, building toward BLACKI
  if (activeTier === 'BLACKI' && milestoneType === 'maximize') {
    return {
      title: 'Move from safe to Old IIMs (ABCLKI)-ready',
      why: 'You have secured the first benchmark. The next step is to strengthen the sections that still keep you short of Old IIMs (ABCLKI).',
      focus: 'Immediate focus: build the next layer without letting the safe base slip.'
    };
  }

  // Case 4: Maximization phase
  if (milestoneType === 'maximize') {
    return {
      title: 'Maximize percentile from a stable base',
      why: 'The visible cutoffs are no longer the main issue. Now the job is to convert more medium opportunities without disturbing your strongest processes.',
      focus: 'Immediate focus: maintain selection discipline and push percentile selectively.'
    };
  }

  // Fallback: use stage_label if available
  if (stage_label) {
    return {
      title: `Focus: ${stage_label}`,
      why: 'Your current performance profile indicates this as the priority area.',
      focus: prioritySection ? `Immediate focus: ${prioritySection}` : 'Immediate focus: balanced improvement across all sections.'
    };
  }

  // Ultimate fallback
  return {
    title: 'Focus on clearing sectional cutoffs',
    why: 'Building a strong foundation across all sections is the first step toward a competitive score.',
    focus: 'Immediate focus: ensure you can consistently clear cutoffs in all three sections.'
  };
};

// v7.4: Resolve AI-authored prose. Prompt now returns a plain string for the active tier.
// Backward-compat: a cached pre-v7.4 report may still hold a `{ NON_BLACKI, BLACKI }` object
// (the post-processor flattens it server-side, but this guards against direct cache reads or
// legacy payloads). We never use it for the off-tier — the off-tier always derives client-side.
const pickProseString = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value.NON_BLACKI || value.BLACKI || null;
  return null;
};

const WhatThisMeansCard = ({ currentPosition, historicalPattern, activeTier, topCards, onOpenEvidence }) => {
  const initialLens = activeTier === 'BLACKI' ? 'BLACKI' : 'NON_BLACKI';
  const [lens, setLens] = useState(initialLens);
  const tierLabel = lens === 'BLACKI' ? 'Old IIMs (ABCLKI)' : 'Other IIMs';

  // v7.4: Active tier shows AI prose (rich diagnostic). Off tier shows derived templated
  // prose (number-grounded, no hollow restatement). Why: AI prose for the off tier was
  // shown to be near-pure numeric restatement — high token cost, low marginal value.
  const isActiveTier = lens === (activeTier || 'NON_BLACKI');
  const aiCurrentPosition = pickProseString(currentPosition);
  const aiHistoricalPattern = pickProseString(historicalPattern);
  const shownCurrentPosition = isActiveTier && aiCurrentPosition
    ? aiCurrentPosition
    : deriveCurrentInterpretation(topCards, lens);
  const shownHistoricalPattern = isActiveTier && aiHistoricalPattern
    ? aiHistoricalPattern
    : `Check the historical trend to see if this ${tierLabel} performance is repeating or improving.`;

  return (
    <section className="sg-card sg-summary-card sg-what-this-means">
      <div className="sg-block-header">
        <h3>Understanding Your Performance</h3>
        <div className="sg-lens-toggle">
          <button className={lens === 'NON_BLACKI' ? 'active' : ''} onClick={() => setLens('NON_BLACKI')}>Other IIMs</button>
          <button className={lens === 'BLACKI' ? 'active' : ''} onClick={() => setLens('BLACKI')}>Old IIMs (ABCLKI)</button>
        </div>
      </div>

      <div className="sg-summary-zones">
        <div className="sg-summary-zone">
          <div className="sg-zone-label">Current position</div>
          <p>{shownCurrentPosition}</p>
        </div>

        <div className="sg-summary-zone">
          <div className="sg-zone-label">Historical pattern</div>
          <p>{shownHistoricalPattern}</p>
        </div>
      </div>

      <div className="sg-summary-actions">
        <button className="sg-inline-toggle" onClick={() => onOpenEvidence({
          title: `${tierLabel} Cutoff Clearing Pattern`,
          lens,
          tableId: 'cutoff_clearing_pattern',
          tierAware: true
        })}>
          View evidence
        </button>
      </div>
    </section>
  );
};

const ImmediateNextGoalCard = ({ goal }) => (
  <section className="sg-card sg-goal-card sg-immediate-goal">
    <h3>Your immediate next goal</h3>
    <div className="sg-goal-card__title">{goal.title}</div>
    <p className="sg-goal-card__why">{goal.why}</p>
    <p className="sg-goal-card__focus">{goal.focus}</p>
  </section>
);



const SectionTabs = ({ sections, evidenceByTier, onOpenEvidence }) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(sections[0]?.title || 'VARC');
  const [openMobile, setOpenMobile] = useState({ [sections[0]?.title || 'VARC']: true });

  if (isMobile) {
    return (
      <section className="sg-card sg-section-deep-dive">
        <h3>Section Deep Dive</h3>
        <div className="sg-accordion-list">
          {sections.map((section) => {
            const isOpen = !!openMobile[section.title];
            return (
              <div key={section.title} className="sg-accordion-item">
                <button
                  className="sg-accordion-header"
                  onClick={() => setOpenMobile((prev) => ({ ...prev, [section.title]: !prev[section.title] }))}
                >
                  <span>{section.title}</span>
                  {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                {isOpen && (
                  <SectionPanel section={section} evidenceByTier={evidenceByTier} onOpenEvidence={onOpenEvidence} />
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section className="sg-card sg-section-deep-dive">
      <div className="sg-block-header">
        <h3>Section Deep Dive</h3>
        <div className="sg-tabs">
          {sections.map((section) => (
            <button
              key={section.title}
              className={`sg-tab ${activeTab === section.title ? 'sg-tab--active' : ''}`}
              onClick={() => setActiveTab(section.title)}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>
      <SectionPanel
        section={sections.find((s) => s.title === activeTab) || sections[0]}
        evidenceByTier={evidenceByTier}
        onOpenEvidence={onOpenEvidence}
      />
    </section>
  );
};

// StructuredFindingRow: renders one analysis block (question_selection, accuracy_and_time, concept_topic_coverage)
// from the structured JSON report — no regex/parsing needed.
const StructuredFindingRow = ({ label, block, sectionKey, onOpenEvidence }) => {
  if (!block) return null;
  // Build current evidence button
  const currentEvidenceId = block.current_evidence;
  // Build historical evidence button
  const historicalEvidenceId = block.historical_evidence;

  // Combine current + historical + advice into a readable text
  const parts = [
    block.current && `**Current:** ${block.current}`,
    block.historical && `**Pattern:** ${block.historical}`,
    block.advice && `**Advice:** ${block.advice}`,
  ].filter(Boolean).join('\n');

  return (
    <div className="sg-finding-row">
      <div className="sg-finding-row__label">{label}</div>
      <div className="sg-finding-row__content">
        <div
          className="sg-finding-row__text"
          dangerouslySetInnerHTML={{ __html: processMarkdown(parts) }}
        />
        <div className="sg-finding-row__actions">
          {currentEvidenceId && (
            <button
              className="sg-inline-toggle"
              onClick={() => onOpenEvidence({
                title: `${sectionKey.toUpperCase()} ${label}`,
                lens: 'NON_BLACKI',
                tableId: currentEvidenceId,
                tierAware: false
              })}
            >
              View evidence
            </button>
          )}
          {historicalEvidenceId && historicalEvidenceId !== currentEvidenceId && (
            <button
              className="sg-inline-toggle sg-inline-toggle--secondary"
              onClick={() => onOpenEvidence({
                title: `${sectionKey.toUpperCase()} ${label} - Historical Pattern`,
                lens: 'NON_BLACKI',
                tableId: historicalEvidenceId,
                tierAware: true,
                // Phase 2: when the post-processor consolidated the legacy
                // `{sec}_type_a_trend` / `{sec}_type_b_trend` into the canonical
                // `{sec}_qn_selection_history`, it stamps `historical_lens` on
                // the block. Surfacing it here lets the modal show which Type's
                // rows are the focus for this finding.
                historicalLens: block.historical_lens || null,
              })}
            >
              View historical pattern
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionPanel = ({ section, evidenceByTier, onOpenEvidence }) => {
  if (!section) return null;

  // section shape from deriveFromStructuredReport:
  // { title, key, strategyContext, questionSelection, accuracyAndTime, conceptTopicCoverage, nextStep }
  const sectionKey = section.key || section.title.toLowerCase();

  return (
    <div className="sg-section-panel">
      {/* Strategy Context */}
      {section.strategyContext && (
        <div className="sg-strategy-context">
          <div className="sg-strategy-context__label">Strategy Context</div>
          <div
            className="sg-strategy-context__text"
            dangerouslySetInnerHTML={{ __html: processMarkdown(section.strategyContext) }}
          />
        </div>
      )}

      {/* Findings Grid - Three structured blocks */}
      <div className="sg-findings-grid">
        <StructuredFindingRow
          label="Question Selection Strategy"
          block={section.questionSelection}
          sectionKey={sectionKey}
          onOpenEvidence={onOpenEvidence}
        />
        <StructuredFindingRow
          label="Time Management"
          block={section.accuracyAndTime}
          sectionKey={sectionKey}
          onOpenEvidence={onOpenEvidence}
        />
        <StructuredFindingRow
          label="Skill Meter"
          block={section.conceptTopicCoverage}
          sectionKey={sectionKey}
          onOpenEvidence={onOpenEvidence}
        />
      </div>

      {/* Next Step */}
      {section.nextStep && (
        <div className="sg-next-step">
          <div className="sg-next-step__label">Next Step</div>
          <div
            className="sg-next-step__text"
            dangerouslySetInnerHTML={{ __html: processMarkdown(section.nextStep) }}
          />
        </div>
      )}
    </div>
  );
};

const NextTwoTestsPlan = ({ nextPlan }) => {
  // nextPlan shape: { immediate_priority, area_priority, tasks[], expected_outcome }
  const tasks = Array.isArray(nextPlan?.tasks) ? nextPlan.tasks : [];

  return (
    <section className="sg-card sg-next-steps">
      <h3>Next 2 tests plan</h3>
      {nextPlan?.immediate_priority && (
        <p className="sg-next-steps__priority" dangerouslySetInnerHTML={{ __html: processMarkdown(nextPlan.immediate_priority) }} />
      )}
      {nextPlan?.area_priority && (
        <p className="sg-next-steps__area-priority" dangerouslySetInnerHTML={{ __html: processMarkdown(nextPlan.area_priority) }} />
      )}
      <div className="sg-priorities">
        {tasks.length ? tasks.map((line, idx) => (
          <div key={idx} className="sg-priority-item" dangerouslySetInnerHTML={{ __html: processMarkdown(line) }} />
        )) : (
          <div className="sg-priority-item">No specific priorities identified.</div>
        )}
      </div>
      {nextPlan?.expected_outcome && (
        <p className="sg-next-steps__outcome" dangerouslySetInnerHTML={{ __html: processMarkdown(nextPlan.expected_outcome) }} />
      )}
    </section>
  );
};

const RatingCard = ({ studentPin, testId }) => {
  const [rating, setRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const detailsRef = useRef(null);

  // Auto-scroll to feedback section on mobile after star selection
  useEffect(() => {
    if (rating > 0 && detailsRef.current && window.innerWidth <= 768) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150); // small delay for render
    }
  }, [rating]);

  const tags = [
    'Accurate analysis',
    'Helpful insights',
    'Clear next steps',
    'Good section breakdown',
    'Missed key points',
    'Too complex',
    'Want more detail'
  ];

  const toggleTag = (tag) =>
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );

  const submit = async () => {
    if (!rating || submitting) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ratings/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentPin,
          testId,
          rating,
          tags: selectedTags,
          comment
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Failed to submit rating:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="sg-card sg-rating-card">
      <h3>Rate This Beta Report</h3>
      <p className="sg-rating-subtitle">Was this report useful? Found anything unclear or incorrect? Please tell us.</p>

      {/* Stars */}
      <div className="sg-rating-stars">
        <div className="sg-rating-stars__row">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              className="sg-rating-star"
              type="button"
            >
              {star <= rating ? '★' : '☆'}
            </button>
          ))}
        </div>
        <p className="sg-rating-text">
          {rating > 0 ? `${rating} star${rating > 1 ? 's' : ''}` : 'Tap to rate'}
        </p>
      </div>

      {/* Tags + Comment + Submit - shown after star selection */}
      {rating > 0 && !submitted && (
        <div className="sg-rating-details sg-rating-details--visible" ref={detailsRef}>
          <p className="sg-rating-question">What stood out? What could be sharper?</p>

          {/* Tags */}
          <div className="sg-rating-tags">
            {tags.map((tag) => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`sg-rating-tag ${selectedTags.includes(tag) ? 'sg-rating-tag--selected' : ''}`}
                type="button"
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Comment */}
          <textarea
            className="sg-rating-comment"
            placeholder="What was most useful? What could be sharper? (optional)"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
          />

          {/* Submit */}
          <button 
            className="sg-rating-submit" 
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      )}

      {/* Success Message */}
      {submitted && (
        <div className="sg-rating-success">
          Thanks! Your feedback helps us improve this beta report.
        </div>
      )}
    </section>
  );
};

const getEvidenceTable = (evidenceByTier, lens, key) => {
  if (!evidenceByTier) return null;
  if (lens && evidenceByTier[lens]?.[key]) return evidenceByTier[lens][key];
  return evidenceByTier?.NON_BLACKI?.[key] || evidenceByTier?.BLACKI?.[key] || null;
};

// Render human-readable bullets derived from `table._ai_meta` factual groups.
//
// Why? The AI narrative is deliberately capped at ONE dense sentence per
// finding (see SIGNAL DENSITY RULE in the prompt) so cards stay scannable.
// But the enricher pre-computes 10+ factual signals PER TABLE (recurring
// topics, regressed topics, Type A bottleneck, largest zero-return bucket,
// priority section, cutoff clearing trend, …) that would otherwise disappear
// into AI reasoning without surfacing to the student. Rendering them below
// the evidence table gives the student the full signal inventory WITHOUT
// bloating every card sentence.
//
// Why an explicit per-table handler instead of a generic serializer?
// The 9 enrichers emit VERY different shapes — some arrays of objects
// (current_topic.notable_topic_misses), some singular objects
// (time_current.largest_zero_return_bucket), some scalars + objects mixed
// (readiness_snapshot with priority_section + priority_reason + gaps).
// A generic "show every non-null field" serializer produces an unreadable
// wall; explicit handlers produce student-friendly phrasing per signal.

// Array-of-objects signals: items with a count and top-5 example labels.
const ARRAY_INSIGHT_DEFS = [
  // current_topic
  { key: 'type_a_unconverted_questions', label: 'Type A questions unconverted',
    format: (q) => `${q.q || '?'} – ${q.topic || '?'}` },
  { key: 'notable_topic_misses', label: 'Topics with 2+ attempts, all missed',
    format: (g) => `${g.topic} (${g.total}/${g.total} lost)` },
  { key: 'notable_topic_strengths', label: 'Topics with 2+ attempts, all correct',
    format: (g) => `${g.topic} (${g.total}/${g.total})` },
  { key: 'high_yield_single_item_misses', label: 'Single Type A questions lost',
    format: (g) => `${g.topic} (${g.type || 'Type A'})` },
  { key: 'slow_wrong_questions', label: 'Slower than topper & wrong',
    format: (q) => `${q.q || '?'} (${q.time_ratio != null ? q.time_ratio.toFixed(1) + '×' : '?'})` },
  // topic_history
  { key: 'recurring_topic_with_current_misses', label: 'Recurring topics still missed this test',
    format: (p) => `${p.topic} (seen in ${p.appearance_count} tests)` },
  { key: 'new_misses_this_test', label: 'First-time topics already missed',
    format: (p) => p.topic },
  { key: 'regressed_topics', label: 'Regressed topics (all-correct before, missed now)',
    format: (p) => p.topic },
  { key: 'improved_topics', label: 'Improved topics (missed before, all-correct now)',
    format: (p) => p.topic },
  { key: 'always_wrong_or_skipped', label: 'Always wrong/skipped (2+ tests, never converted)',
    format: (p) => `${p.topic} (${p.appearance_count} tests)` },
  // qn_selection_snapshot
  { key: 'type_a_opportunity_areas', label: 'Type A marks left on table (by area)',
    format: (a) => `${a.area} (${a.missed_marks} marks)` },
  { key: 'type_b_opportunity_areas', label: 'Type B marks left on table (by area)',
    format: (a) => `${a.area} (${a.missed_marks} marks)` },
  { key: 'type_c_risk_reward_context', label: 'Type C marks left (by area)',
    format: (a) => `${a.area} (${a.missed_marks} marks)` },
  // time_current
  { key: 'buckets_with_marks_returned', label: 'Areas where time earned marks',
    format: (b) => `${b.area || '?'} ${b.type || ''} (${b.marks} marks in ${b.time_min} min)` },
];

// Singular-object / scalar signals: presented as label + detail (no count).
const SINGLETON_INSIGHT_DEFS = [
  // qn_selection_snapshot statuses
  { key: 'type_a_status', label: 'Type A status',
    format: (s) => {
      if (!s || s.available == null) return null;
      const bits = [`${s.attempted}/${s.available} attempted`];
      if (s.correct != null) bits.push(`${s.correct} correct`);
      if (s.marks_left_on_table != null && s.marks_left_on_table > 0)
        bits.push(`${s.marks_left_on_table} marks left`);
      if (s.bottleneck && s.bottleneck !== 'none') {
        const bottleneckMap = {
          both: 'Issue: low attempts + low accuracy',
          selection: 'Issue: low attempts',
          accuracy: 'Issue: low accuracy',
        };
        bits.push(bottleneckMap[s.bottleneck] || `${s.bottleneck}`);
      }
      return bits.join(' • ');
    } },
  { key: 'type_b_status', label: 'Type B status',
    format: (s) => {
      if (!s || s.available == null) return null;
      const bits = [`${s.attempted}/${s.available} attempted`];
      if (s.correct != null) bits.push(`${s.correct} correct`);
      if (s.marks_left_on_table != null && s.marks_left_on_table > 0)
        bits.push(`${s.marks_left_on_table} marks left`);
      if (s.bottleneck && s.bottleneck !== 'none') {
        const bottleneckMap = {
          both: 'Issue: low attempts + low accuracy',
          selection: 'Issue: low attempts',
          accuracy: 'Issue: low accuracy',
        };
        bits.push(bottleneckMap[s.bottleneck] || `${s.bottleneck}`);
      }
      return bits.join(' • ');
    } },
  { key: 'type_c_status', label: 'Type C: marks still available',
    format: (s) => {
      if (!s || s.available == null) return null;
      const bits = [`${s.attempted}/${s.available} attempted`];
      if (s.marks_left_on_table != null && s.marks_left_on_table > 0)
        bits.push(`${s.marks_left_on_table} unclaimed`);
      return bits.join(' • ');
    } },
  // time_current
  { key: 'largest_zero_return_bucket', label: 'Largest time area with no marks returned',
    format: (b) => b ? `${b.area || '?'} ${b.type || ''} — ${b.time_min} min, 0 marks` : null },
  { key: 'largest_time_absorption_bucket', label: 'Area where you spent the most time',
    format: (b) => b ? `${b.area || '?'} ${b.type || ''} — ${b.time_min} min → ${b.marks} marks` : null },
  // readiness_snapshot (cutoff_status)
  { key: 'overall_status', label: 'Overall status',
    format: (s) => (typeof s === 'string' ? s : null) },
  { key: 'priority_reason', label: 'Priority section',
    format: (s) => (typeof s === 'string' ? s : null) },
  // readiness_history (cutoff_clearing_pattern)
  { key: 'never_cleared_any_section', label: 'Cutoff clearance history',
    format: (v) => v === true ? 'Never cleared any sectional cutoff across the tests analyzed'
                  : v === false ? 'Has cleared at least one sectional cutoff before' : null },

  // ── Time Trend Type (time_trend_type) ──────────────────────────────────
  // Why per-type MPM comparison: the enricher computes current vs prior avg
  // for each type. Surfacing the delta as a plain-English line saves the
  // student from scanning rows × columns to spot trends.
  { key: 'time_trend_type_a_mpm', label: 'Type A MPM trend',
    format: (s) => renderMpmTrend('Type A', s) },
  { key: 'time_trend_type_b_mpm', label: 'Type B MPM trend',
    format: (s) => renderMpmTrend('Type B', s) },
  { key: 'time_trend_type_c_mpm', label: 'Type C MPM trend',
    format: (s) => renderMpmTrend('Type C', s) },

  // ── Time Trend Area (time_trend_area) ──────────────────────────────────
  // Why: the enricher flags the area with highest current time that returns
  // below-average MPM — a factual efficiency drain visible only cross-area.
  { key: 'largest_time_absorption_area_trend', label: 'Area absorbing most time',
    format: (a) => a && a.area
      ? `${a.area} — ${a.current_time_min != null ? a.current_time_min.toFixed(1) : '?'} min spent, MPM ${a.current_mpm != null ? a.current_mpm.toFixed(1) : '?'} (vs. section avg ${a.avg_mpm != null ? a.avg_mpm.toFixed(1) : '?'})`
      : null },

  // ── Qn Selection History (qn_selection_history) ───────────────────────
  // Why per-type selection/accuracy comparison: the enricher computes
  // current versus prior-average stats per type. Surfacing the delta as
  // a short line saves the student from comparing columns across rows.
  { key: 'qn_sel_history_type_a', label: 'Type A selection & accuracy',
    format: (s) => renderQnSelTrend('Type A', s) },
  { key: 'qn_sel_history_type_b', label: 'Type B selection & accuracy',
    format: (s) => renderQnSelTrend('Type B', s) },
  { key: 'qn_sel_history_type_c', label: 'Type C selection & accuracy',
    format: (s) => renderQnSelTrend('Type C', s) },
  { key: 'qn_sel_history_cutoff', label: 'Cutoff clearance streak',
    format: (s) => {
      if (!s || typeof s !== 'object') return null;
      const parts = [];
      if (s.current_status) parts.push(`Current: ${s.current_status === 'CLEARED' ? '✅ Cleared' : '❌ Missed'}`);
      if (s.cleared_count != null && s.missed_count != null && (s.cleared_count + s.missed_count) > 0) {
        parts.push(`Across last ${s.cleared_count + s.missed_count} tests: cleared ${s.cleared_count}, missed ${s.missed_count}`);
      }
      return parts.join(' • ') || null;
    } },
];

// Scalar counts of interest (shown only when > 0).
const COUNT_INSIGHT_DEFS = [
  { key: 'zero_return_bucket_count', label: 'Time spent with no marks' },
  { key: 'negative_mpm_bucket_count', label: 'Time that cost marks' },
];

const buildUsefulInsightItems = (meta) => {
  if (!meta || typeof meta !== 'object') return [];
  const items = [];

  for (const def of ARRAY_INSIGHT_DEFS) {
    const arr = meta[def.key];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const detail = arr.slice(0, 5).map(def.format).filter(Boolean).join(', ') +
      (arr.length > 5 ? `, +${arr.length - 5} more` : '');
    items.push({ label: def.label, count: arr.length, detail });
  }

  for (const def of SINGLETON_INSIGHT_DEFS) {
    if (!(def.key in meta)) continue;
    const rendered = def.format(meta[def.key]);
    if (rendered == null || rendered === '') continue;
    items.push({ label: def.label, count: null, detail: rendered });
  }

  for (const def of COUNT_INSIGHT_DEFS) {
    const n = meta[def.key];
    if (typeof n !== 'number' || n <= 0) continue;
    items.push({ label: def.label, count: n, detail: null });
  }

  return items;
};

// ── _ai_meta pre-processing bridge ──────────────────────────────────────
// Why: the enrichers emit raw scalar keys (type_a_current_mpm, …) but the
// SINGLETON_INSIGHT_DEFS expect a single composite object per insight line.
// This bridge synthesises those composite keys so definitions stay clean.
const preprocessMeta = (rawMeta) => {
  if (!rawMeta || typeof rawMeta !== 'object') return rawMeta;
  const out = { ...rawMeta };

  // ── Time Trend Type ──
  // Why per_test[1] (immediate prior) instead of prior_tests_avg_mpm:
  // same rationale as Qn Selection History — the table is row-by-test,
  // so "prior" means the test row immediately before current.
  if (typeof rawMeta.type_a_current_mpm === 'number') {
    for (const tn of ['type_a', 'type_b', 'type_c']) {
      const letter = tn.charAt(tn.length - 1); // 'a','b','c'
      const mpmTrend = rawMeta[`${tn}_mpm_trend`];
      out[`time_trend_type_${letter}_mpm`] = {
        current: rawMeta[`${tn}_current_mpm`],
        prior: (Array.isArray(mpmTrend) && mpmTrend.length > 1 && mpmTrend[1] != null)
          ? mpmTrend[1]
          : rawMeta[`${tn}_prior_tests_avg_mpm`],
        priorAvailable: rawMeta[`${tn}_prior_baseline_available`],
      };
    }
  }

  // ── Time Trend Area ──
  const areaName = rawMeta.largest_time_absorption_area;
  if (typeof areaName === 'string' && rawMeta.areas && rawMeta.areas[areaName]) {
    const a = rawMeta.areas[areaName];
    const areaNames = Object.keys(rawMeta.areas);
    const allMpms = areaNames.map(n => rawMeta.areas[n]?.current_mpm).filter(v => v != null && !isNaN(v));
    const avgMpm = allMpms.length > 0 ? allMpms.reduce((s, v) => s + v, 0) / allMpms.length : null;
    out.largest_time_absorption_area_trend = {
      area: areaName,
      current_time_min: a.current_time_min,
      current_mpm: a.current_mpm,
      avg_mpm: avgMpm,
    };
  }

  // ── Qn Selection History ──
  // Why use per_test[1] instead of prior_*_avg: the table shows each test
  // row in chronological order. When a student sees "prior" in the _ai_meta
  // section, they mentally compare it to the row immediately before current —
  // not an average across all historical rows. Using the immediate prior
  // (index 1 of the per_test array) keeps the number consistent with what
  // they can spot-check in the table.
  if (typeof rawMeta.type_a_current_selection_pct === 'number') {
    for (const tn of ['type_a', 'type_b', 'type_c']) {
      const letter = tn.charAt(tn.length - 1);
      const selPerTest = rawMeta[`${tn}_selection_pct_per_test`];
      const accPerTest = rawMeta[`${tn}_accuracy_pct_per_test`];
      out[`qn_sel_history_type_${letter}`] = {
        currentSel: rawMeta[`${tn}_current_selection_pct`],
        priorSel: (Array.isArray(selPerTest) && selPerTest.length > 1 && selPerTest[1] != null)
          ? selPerTest[1]
          : rawMeta[`${tn}_prior_selection_pct_avg`],
        currentAcc: rawMeta[`${tn}_current_accuracy_pct`],
        priorAcc: (Array.isArray(accPerTest) && accPerTest.length > 1 && accPerTest[1] != null)
          ? accPerTest[1]
          : rawMeta[`${tn}_prior_accuracy_pct_avg`],
        priorAvailable: rawMeta[`${tn}_prior_baseline_available`],
      };
    }
    if (rawMeta.cutoff_history_summary) {
      out.qn_sel_history_cutoff = rawMeta.cutoff_history_summary;
    }
  }

  return out;
};

const renderMpmTrend = (typeLabel, s) => {
  if (!s || typeof s !== 'object') return null;
  if (s.current == null) return null;
  let line = `${typeLabel} MPM: ${s.current.toFixed(1)}`;
  if (s.prior != null) {
    const delta = s.current - s.prior;
    const arrow = delta > 0.03 ? ' ↑' : delta < -0.03 ? ' ↓' : ' →';
    line += ` (prior ${s.prior.toFixed(1)}${arrow})`;
  }
  return line;
};

const renderQnSelTrend = (typeLabel, s) => {
  if (!s || typeof s !== 'object') return null;
  const parts = [];
  if (s.currentSel != null) {
    let line = `Sel ${s.currentSel.toFixed(0)}%`;
    if (s.priorSel != null) {
      const d = s.currentSel - s.priorSel;
      line += d > 1 ? ' ↑' : d < -1 ? ' ↓' : ' →';
      line += ` (prior ${s.priorSel.toFixed(0)}%)`;
    }
    parts.push(line);
  }
  if (s.currentAcc != null) {
    let line = `Acc ${s.currentAcc.toFixed(0)}%`;
    if (s.priorAcc != null) {
      const d = s.currentAcc - s.priorAcc;
      line += d > 1 ? ' ↑' : d < -1 ? ' ↓' : ' →';
      line += ` (prior ${s.priorAcc.toFixed(0)}%)`;
    }
    parts.push(line);
  }
  return parts.length > 0 ? `${typeLabel}: ${parts.join(' · ')}` : null;
};

const UsefulInsights = ({ table }) => {
  const meta = preprocessMeta(table?._ai_meta);
  if (!meta) return null;

  const items = buildUsefulInsightItems(meta);
  if (!items.length) return null;

  return (
    <div className="sg-useful-insights">
      <div className="sg-useful-insights__title">Useful insights from this table</div>
      <div className="sg-useful-insights__grid">
        {items.map((it, i) => (
          <div key={i} className="sg-useful-insights__item">
            <strong>{it.label}</strong>
            {it.count != null && (
              <span className="sg-useful-insights__count"> ({it.count})</span>
            )}
            {it.detail && <span className="sg-useful-insights__detail">: {it.detail}</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

// Resolve a requested modal descriptor into a normalized view-model that the
// UI can render directly. Handles three modes without leaking the logic into
// the component body:
//   1. Paired card (TM History): explicit `pairedKey` from Library, OR auto-
//      detected from a `*_time_trend_{type,area}` tableId so per-claim
//      "View historical pattern" buttons also surface the toggle.
//   2. Legacy table id: remap to canonical replacement + set a notice so the
//      student sees WHY they're looking at a different table.
//   3. Plain canonical table: pass through unchanged.
const resolveModalView = (modal) => {
  if (!modal) return null;
  // Case 1a: explicit paired card
  if (modal.pairedKey && PAIRED_TABLES[modal.pairedKey]) {
    const paired = PAIRED_TABLES[modal.pairedKey];
    return {
      mode: 'paired',
      title: modal.title || paired.label,
      tabs: paired.tabs,
      defaultTabKey: modal.activeTab || paired.tabs[0].key,
      tierAware: false,
      notice: null,
    };
  }
  // Case 1b: auto-upgrade TM trend table → paired modal
  const autoPair = getTimeHistoryPairing(modal.tableId);
  if (autoPair && PAIRED_TABLES[autoPair.pairedKey]) {
    const paired = PAIRED_TABLES[autoPair.pairedKey];
    return {
      mode: 'paired',
      title: modal.title || paired.label,
      tabs: paired.tabs,
      defaultTabKey: autoPair.activeTab,
      tierAware: false,
      notice: null,
    };
  }
  // Case 2: legacy id → remap with notice
  if (modal.tableId && isLegacyTable(modal.tableId)) {
    const remapped = resolveLegacyRemap(modal.tableId);
    if (remapped) {
      return {
        mode: 'single',
        title: modal.title,
        tableId: remapped,
        tierAware: !!modal.tierAware,
        notice: `Consolidated into ${getCanonicalLabel(remapped)}.`,
      };
    }
    return {
      mode: 'empty',
      title: modal.title,
      notice: 'This evidence table has been retired. See the Evidence Library for the current equivalents.',
    };
  }
  // Case 3: plain
  return {
    mode: 'single',
    title: modal.title,
    tableId: modal.tableId,
    tierAware: !!modal.tierAware,
    notice: null,
    historicalLens: modal.historicalLens || null,
  };
};

const EvidenceModal = ({ modal, evidenceByTier, onClose }) => {
  const [lens, setLens] = useState(modal?.lens || 'NON_BLACKI');
  const [activeTab, setActiveTab] = useState(null);

  const view = resolveModalView(modal);

  useEffect(() => {
    setLens(modal?.lens || 'NON_BLACKI');
    const nextView = resolveModalView(modal);
    if (nextView?.mode === 'paired') setActiveTab(nextView.defaultTabKey);
    else setActiveTab(null);
  }, [modal]);

  if (!modal || !view) return null;

  // Resolve the concrete table to render based on mode.
  let table = null;
  if (view.mode === 'single') {
    table = getEvidenceTable(evidenceByTier, view.tierAware ? lens : null, view.tableId);
  } else if (view.mode === 'paired') {
    const currentTabKey = activeTab || view.defaultTabKey;
    const tabDef = view.tabs.find((t) => t.key === currentTabKey) || view.tabs[0];
    table = getEvidenceTable(evidenceByTier, null, tabDef.tableId);
  }

  return (
    <div className="sg-modal-backdrop">
      <div className="sg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sg-modal__header">
          <div className="sg-modal__header-actions">
            {/* Paired tab control (By Type / By Area) — in header row next to close button
                to avoid stretching when it sits alone as a direct child of .sg-modal (flex column). */}
            {view.mode === 'paired' && (
              <div className="sg-lens-toggle sg-lens-toggle--tabs">
                {view.tabs.map((tab) => {
                  const key = activeTab || view.defaultTabKey;
                  return (
                    <button
                      key={tab.key}
                      className={key === tab.key ? 'active' : ''}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}
            {view.tierAware && (
              <div className="sg-lens-toggle">
                <button className={lens === 'NON_BLACKI' ? 'active' : ''} onClick={() => setLens('NON_BLACKI')}>Other IIMs</button>
                <button className={lens === 'BLACKI' ? 'active' : ''} onClick={() => setLens('BLACKI')}>Old IIMs (ABCLKI)</button>
              </div>
            )}
          </div>
          <button className="sg-modal__close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="sg-modal__body">
          {view.notice && (
            <div className="sg-modal__notice" style={{ padding: '8px 12px', marginBottom: '8px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: '6px', fontSize: '13px', color: '#874d00' }}>
              {view.notice}
            </div>
          )}
          {view.historicalLens && view.mode === 'single' && (
            /* Phase 2: the post-processor pins a specific Type lens on
               qn_selection_history references. Surface it so the student
               knows which rows in the consolidated table are the focus. */
            <div className="sg-modal__notice" style={{ padding: '8px 12px', marginBottom: '8px', background: '#e6f4ff', border: '1px solid #91caff', borderRadius: '6px', fontSize: '13px', color: '#003a8c' }}>
              Focus: {view.historicalLens === 'type_b' ? 'Type B' : 'Type A'} rows (historical lens).
            </div>
          )}
          {view.mode === 'empty' || !table ? (
            <div className="sg-empty">No evidence available.</div>
          ) : (
            <>
              <EvidenceTable table={table} />
              <UsefulInsights table={table} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Render a cell value with \n → visual line breaks. Used by both desktop <td>
// and mobile card rows so the two layouts stay consistent.
//
// Why a shared helper? The backend emits plain \n in cell strings (e.g.
// "71.4%\n40.0%" or "✅ CLEARED\n11/24"). CSS white-space: pre-line handles
// the desktop break; mobile cards need explicit <span> stacking for layout control.
const renderCellValue = (cell) => {
  const s = String(cell ?? '—');
  const lines = s.split('\n');
  if (lines.length <= 1) return s;
  return (
    <span className="sg-cell--multiline">
      {lines.map((line, i) => (
        <span key={i} className="sg-cell--multiline-line">{line}</span>
      ))}
    </span>
  );
};

// Strip "Q" prefix from question-number cells ("Q10" → "10") to prevent
// line-wrapping in narrow desktop table columns. Mobile cards are unaffected.
const stripQPrefix = (cell) => {
  const s = String(cell ?? '');
  return s.replace(/^Q(\d+)$/, '$1');
};

// Mobile-specific cell renderer for readiness status cells.
// Desktop gets Option B (2-line: status + "26/70 — gap 44").
// Mobile gets Option C (3-line: status + "Scored: 26" + "Cutoff: 70 · gap 44").
//
// Why separate? Mobile cards have generous vertical space; a 3-line layout
// with labelled rows is more readable than the compact desktop format.
// Parses the backend's 2-line Option B format to extract scored/cutoff/gap.
const renderMobileCellValue = (cell) => {
  const s = String(cell ?? '—');
  // Match the new readiness format: "✅ CLEARED\n30/29 — safe by 1"
  // or "❌ MISSED\n26/70 — gap 44"
  const readinessMatch = s.match(/^([✅❌]\s*(?:CLEARED|MISSED))\n(\d+)\/(\d+)\s*[—–-]\s*(safe by|gap)\s*(\d+)$/);
  if (readinessMatch) {
    const [, status, scored, cutoff, hintType, gapVal] = readinessMatch;
    return (
      <span className="sg-cell--multiline">
        <span className="sg-cell--multiline-line">{status}</span>
        <span className="sg-cell--multiline-line sg-cell--readiness-scored">Scored: {scored}</span>
        <span className="sg-cell--multiline-line sg-cell--readiness-cutoff">
          Cutoff: {cutoff} · {hintType} {gapVal}
        </span>
      </span>
    );
  }
  // For all other cells (regular multiline, numeric, text), use the standard helper.
  return renderCellValue(cell);
};

// Parse a cell value into a sortable primitive. Layered by kind (Q#, MM:SS,
// ratio, percent, number, string) so sort doesn't lie on numeric columns
// (e.g. 'Q10' < 'Q2' in string compare). Nulls / dashes / 'N/A' always sort
// to the bottom regardless of direction.
//
// Multiline cells (e.g. "71.4%\n40.0%", "✅ CLEARED\n11/24"): extract the
// first line only for numeric parsing. Second-line values (score/cutoff for
// readiness, marks+time for MPM) are secondary context — sorting by the
// primary first-line metric gives the most useful ordering. Status-first cells
// (CLEARED/MISSED) sort lexicographically by status, which is acceptable.
const parseCellForSort = (raw) => {
  if (raw == null) return { kind: 'null', val: null };
  if (typeof raw === 'number') return { kind: 'num', val: raw };
  const s = String(raw).trim();
  if (s === '' || s === '—' || s === '-' || s === 'N/A') return { kind: 'null', val: null };
  // For multiline cells, sort by the first line only (the primary metric).
  const firstLine = s.split('\n')[0].trim();
  const qMatch = firstLine.match(/^Q(\d+)$/);
  if (qMatch) return { kind: 'num', val: parseInt(qMatch[1], 10) };
  const timeMatch = firstLine.match(/^(\d+):(\d{2})$/);
  if (timeMatch) return { kind: 'num', val: parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10) };
  const ratioMatch = firstLine.match(/^([\d.]+)[×x]$/);
  if (ratioMatch) return { kind: 'num', val: parseFloat(ratioMatch[1]) };
  const pctMatch = firstLine.match(/^([\d.]+)%$/);
  if (pctMatch) return { kind: 'num', val: parseFloat(pctMatch[1]) };
  const n = Number(firstLine);
  if (!Number.isNaN(n) && /^-?\d+(\.\d+)?$/.test(firstLine)) return { kind: 'num', val: n };
  // Status-first cells (e.g. "✅ CLEARED\n11/24") sort by status text.
  return { kind: 'str', val: firstLine.toLowerCase() };
};

const makeComparator = (colIndex, dir) => (rowA, rowB) => {
  const a = parseCellForSort(rowA[colIndex]);
  const b = parseCellForSort(rowB[colIndex]);
  if (a.kind === 'null' && b.kind === 'null') return 0;
  if (a.kind === 'null') return 1;
  if (b.kind === 'null') return -1;
  let cmp;
  if (a.kind === 'num' && b.kind === 'num') cmp = a.val - b.val;
  else cmp = String(a.val).localeCompare(String(b.val));
  return dir === 'desc' ? -cmp : cmp;
};

// Single evidence table with desktop table + mobile card layout.
// Backported from audit-viewer (WAVE 2): click-to-sort headers, multi-line
// header labels, optional purpose row, optional cellTooltips sidecar, and an
// 'Overall' row heuristic. All extras degrade gracefully when the backend
// doesn't emit them, so existing tables keep working unchanged.
const EvidenceTable = ({ table }) => {
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  const columns = Array.isArray(table?.columns) ? table.columns : [];
  // cellTooltips is a 2D array parallel to rows. When present,
  // cellTooltips[r][c] is shown as native hover title on that <td>. Sidecar
  // (not embedded in row) keeps the row schema flat strings end-to-end.
  const cellTooltips = Array.isArray(table?.cellTooltips) ? table.cellTooltips : null;
  const isSimplePattern = table?.isSimplePattern;

  // Sort state: { col, dir } | null. null preserves builder's default order.
  const [sort, setSort] = useState(null);
  // Mobile source hint toggle — collapsed by default to save vertical space
  // below already-dense card stacks. Desktop always shows inline.
  const [showSource, setShowSource] = useState(false);
  // Purpose panel toggle — ⓘ button opens a readable panel below the title.
  const [showPurpose, setShowPurpose] = useState(false);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return rows.slice().sort(makeComparator(sort.col, sort.dir));
  }, [rows, sort]);

  // Track authored-row indices alongside the sort so cellTooltips (indexed
  // by authored order) follow their paired row through any sort. Sorting
  // index-paired entries is O(n log n) and handles duplicate cell values
  // that would make indexOf-based lookup ambiguous.
  const sortedOrigIdx = useMemo(() => {
    const base = rows.map((_, i) => i);
    if (!sort) return base;
    const cmp = makeComparator(sort.col, sort.dir);
    return base.slice().sort((ai, bi) => cmp(rows[ai], rows[bi]));
  }, [rows, sort]);

  // Click cycle: unsorted → asc → desc → unsorted. Three-state keeps the
  // builder's default always one click away. Clicking a DIFFERENT column
  // starts its own cycle from asc.
  const onHeaderClick = (colIndex) => {
    setSort((prev) => {
      if (!prev || prev.col !== colIndex) return { col: colIndex, dir: 'asc' };
      if (prev.dir === 'asc') return { col: colIndex, dir: 'desc' };
      return null;
    });
  };
  const chevronFor = (colIndex) => {
    if (!sort || sort.col !== colIndex) return '↕';
    return sort.dir === 'asc' ? '↑' : '↓';
  };
  const isActiveSort = (colIndex) => sort && sort.col === colIndex;

  const getCellClass = (cell, colIndex) => {
    const s = String(cell ?? '').toUpperCase().trim();
    // Simple-pattern tables: column 1 is the dedicated status column.
    if (isSimplePattern && colIndex === 1) {
      return s === 'CLEARED' ? 'sg-status-cleared' : s === 'MISSED' ? 'sg-status-missed' : '';
    }
    // All other tables: detect CLEARED/MISSED keywords with word boundaries,
    // e.g. readiness cells like "✅ CLEARED\n30/29 — safe by 1".
    if (/\bCLEARED\b/i.test(s) && !/\bUNCLEARED\b/i.test(s)) return 'sg-status-cleared';
    if (/\bMISSED\b/i.test(s)) return 'sg-status-missed';
    return '';
  };

  return (
    <div className="sg-evidence-table">
      {/* Title row: title (WHAT) centered, subtle ⓘ button if purpose exists.
          Click opens a readable purpose panel below. No fragile hover tooltips. */}
      <div className="sg-evidence-table__header">
        <div className="sg-evidence-table__title-row">
          <div className="sg-evidence-table__title">
            {table?.title || 'Evidence table'}
          </div>
          {table?.purpose && (
            <button
              type="button"
              className={`sg-evidence-table__info-btn${showPurpose ? ' sg-evidence-table__info-btn--active' : ''}`}
              onClick={() => setShowPurpose((v) => !v)}
              aria-expanded={showPurpose}
              aria-label="Show table explanation"
            >
              ⓘ
            </button>
          )}
        </div>
        {table?.purpose && showPurpose && (
          <div className="sg-evidence-table__purpose-panel">
            {table.purpose}
          </div>
        )}
        {table?.subtitle && (() => {
          // '\n'-joined subtitles (e.g. Current Qn. Selection's score + cutoffs)
          // stack as centered multi-line prominent lines. Single-line falls back
          // to the quieter legacy style.
          const subtitleStr = String(table.subtitle);
          const lines = subtitleStr.split('\n').filter(Boolean);
          if (lines.length > 1) {
            return (
              <div className="sg-evidence-table__subtitle sg-evidence-table__subtitle--multiline">
                {lines.map((line, i) => (
                  <div key={i} className="sg-evidence-table__subtitle-line">{line}</div>
                ))}
              </div>
            );
          }
          return <div className="sg-evidence-table__subtitle">{subtitleStr}</div>;
        })()}
      </div>

      {/* Desktop: sortable table */}
      <div className="sg-evidence-table__desktop">
        <table className={`sg-table ${isSimplePattern ? 'sg-table--simple-pattern' : ''}`}>
          <thead>
            <tr>
              {columns.map((c, i) => {
                // Multi-line <th>: '\n' in column label → stacked sub-labels
                // (e.g. 'Type A' + 'Sel %' + 'Acc %'). Each sub-label gets its
                // own hover tooltip via a short expand map.
                const labelStr = String(c ?? '');
                const labelLines = labelStr.split('\n');
                const hasSubLabel = labelLines.length > 1;
                const expandSubLabel = (s) =>
                  s
                    .replace(/\bSel\b/gi, 'Selection')
                    .replace(/\bAcc\b/gi, 'Accuracy');
                return (
                  <th
                    key={i}
                    onClick={() => onHeaderClick(i)}
                    className={`sg-table__th--sortable${isActiveSort(i) ? ' sg-table__th--active' : ''}${hasSubLabel ? ' sg-table__th--multiline' : ''}`}
                    aria-sort={isActiveSort(i) ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    title="Click to sort (toggle asc → desc → default)"
                  >
                    {hasSubLabel ? (
                      <span className="sg-table__th-label">
                        <span className="sg-table__th-label-primary">{labelLines[0]}</span>
                        {labelLines.slice(1).map((subLine, si) => (
                          <span
                            key={si}
                            className="sg-table__th-label-sub"
                            title={expandSubLabel(subLine)}
                          >
                            {subLine}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="sg-table__th-label">{labelStr}</span>
                    )}
                    <span className="sg-table__th-chevron">{chevronFor(i)}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, r) => {
              const origIdx = sortedOrigIdx[r];
              // 'Overall' heuristic: row whose first or second cell is literally
              // 'Overall' is a group total → bolded as a section divider.
              const isOverallRow =
                String(row?.[0] ?? '').trim() === 'Overall' ||
                String(row?.[1] ?? '').trim() === 'Overall';
              const highlightCls = table?.highlightFirstRow && r === 0 ? 'sg-table__row--highlight' : '';
              const overallCls = isOverallRow ? 'sg-table__row--overall' : '';
              const rowCls = [highlightCls, overallCls].filter(Boolean).join(' ');
              return (
                <tr key={r} className={rowCls}>
                  {row.map((cell, c) => {
                    const tip = cellTooltips?.[origIdx]?.[c];
                    return (
                      <td
                        key={c}
                        className={getCellClass(cell, c)}
                        title={tip || undefined}
                      >
                        {renderCellValue(stripQPrefix(cell))}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile: card layout, no sort UI (cards are a scannable format).
          Uses sortedRows so desktop sort persists across a viewport resize. */}
      <div className="sg-evidence-table__mobile">
        {sortedRows.map((row, r) => {
          const origIdx = sortedOrigIdx[r];
          const isHighlighted = table?.highlightFirstRow && r === 0;
          const isOverallRow =
            String(row?.[0] ?? '').trim() === 'Overall' ||
            String(row?.[1] ?? '').trim() === 'Overall';
          const cardCls = [
            'sg-evidence-card',
            isHighlighted ? 'sg-evidence-card--highlight' : '',
            isOverallRow ? 'sg-evidence-card--overall' : '',
          ].filter(Boolean).join(' ');
          return (
            <div key={r} className={cardCls}>
              <div className="sg-evidence-card__header">
                <div className="sg-evidence-card__title">{row[0] || '—'}</div>
                {row[1] && <div className="sg-evidence-card__subtitle">{row[1]}</div>}
                {isHighlighted && <span className="sg-evidence-card__badge">Current</span>}
              </div>
              <div className="sg-evidence-card__body">
                {/* Two-column paired layout — halves vertical height.
                    Odd column count: last item takes full width.
                    On ≤420px: CSS collapses to single column. */}
                {(() => {
                  const metricCols = columns.slice(2);
                  const pairs = [];
                  for (let i = 0; i < metricCols.length; i += 2) {
                    pairs.push(metricCols.slice(i, i + 2));
                  }
                  return pairs.map((pair, pairIdx) => {
                    const baseIdx = pairIdx * 2;
                    return (
                      <div key={pairIdx} className={`sg-evidence-card__row-pair${pair.length === 1 ? ' sg-evidence-card__row-pair--single' : ''}`}>
                        {pair.map((col, ci) => {
                          const colIndex = baseIdx + ci + 2;
                          const labelStr = String(col ?? '');
                          const flatLabel = labelStr.replace(/\n/g, ' · ');
                          const tip = cellTooltips?.[origIdx]?.[colIndex];
                          return (
                            <div key={ci} className="sg-evidence-card__cell" title={tip || undefined}>
                              <span className="sg-evidence-card__label">{flatLabel}</span>
                              <span className={`sg-evidence-card__value ${getCellClass(row[colIndex], colIndex)}`}>
                                {renderMobileCellValue(row[colIndex])}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          );
        })}
      </div>

      {table?.sourceHint && (
        <div className="sg-evidence-table__source">
          <button
            className="sg-evidence-table__source-toggle"
            onClick={() => setShowSource((v) => !v)}
            aria-expanded={showSource}
          >
            <span>Where is this data from?</span>
            <span className={`sg-evidence-table__source-chevron${showSource ? ' sg-evidence-table__source-chevron--open' : ''}`}>▾</span>
          </button>
          {showSource && (
            <div className="sg-evidence-table__source-text">{table.sourceHint}</div>
          )}
        </div>
      )}
    </div>
  );
};

// Evidence Library Modal — split-pane layout (desktop) + single-pane
// drill-down (mobile). Backported from audit-viewer.
//
// Why split-pane: the old full-width accordion list forced users to lose
// context each time they "opened" a table (second modal stacked on top).
// The split-pane keeps the taxonomy visible alongside the selected table so
// switching cards is a single click with no backdrop juggling.
//
// Right pane handles three modes: placeholder (nothing selected), paired
// (TM History → Types⇄Areas toggle), and single (plain canonical table).
// Legacy IDs do not appear here because LIBRARY_CATEGORIES is curated.
const EvidenceLibraryModal = ({ evidenceByTier, onClose, fullPage = false }) => {
  const [lens, setLens] = useState('NON_BLACKI');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [mobileView, setMobileView] = useState('nav'); // 'nav' | 'content'
  const categories = LIBRARY_CATEGORIES;

  // Tier toggle is shown only when the selected entry is tier-aware.
  // Paired entries (TM History) are tier-neutral so the toggle hides on their selection.
  const showTierToggle = !!selectedEntry?.tierAware;

  const handlePick = (entry) => {
    setSelectedEntry(entry);
    setActiveTab(null);
    setMobileView('content');
  };
  const handleBack = () => { setMobileView('nav'); setSelectedEntry(null); };

  // Resolve the concrete table for the right pane based on selection mode.
  // Keeps all the lookup logic in one place so the render body stays flat.
  const renderContent = () => {
    if (!selectedEntry) {
      return (
        <div className="sg-library-placeholder">
          <div className="sg-library-placeholder__title">Pick a table from the left to view evidence</div>
          <div className="sg-library-placeholder__hint">
            Tables with no data for this test are disabled in the nav.
          </div>
        </div>
      );
    }
    if (selectedEntry.pairedKey && PAIRED_TABLES[selectedEntry.pairedKey]) {
      const paired = PAIRED_TABLES[selectedEntry.pairedKey];
      const tabKey = activeTab || paired.tabs[0].key;
      const tabDef = paired.tabs.find((t) => t.key === tabKey) || paired.tabs[0];
      const pairedTable = getEvidenceTable(evidenceByTier, null, tabDef.tableId);
      return (
        <>
          <div className="sg-lens-toggle sg-lens-toggle--tabs" style={{ marginBottom: '0.75rem' }}>
            {paired.tabs.map((tab) => (
              <button
                key={tab.key}
                className={tabKey === tab.key ? 'active' : ''}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {pairedTable ? (
            <>
              <EvidenceTable table={pairedTable} />
              <UsefulInsights table={pairedTable} />
            </>
          ) : <div className="sg-empty">No evidence available for this view.</div>}
        </>
      );
    }
    const singleTable = getEvidenceTable(
      evidenceByTier,
      selectedEntry.tierAware ? lens : null,
      selectedEntry.id
    );
    return singleTable
      ? (
        <>
          <EvidenceTable table={singleTable} />
          <UsefulInsights table={singleTable} />
        </>
      )
      : <div className="sg-empty">No evidence available.</div>;
  };

  const content = (
    <div
      className={fullPage 
        ? `sg-library-fullpage ${mobileView === 'content' ? 'sg-library-fullpage--content-view' : ''}`
        : `sg-modal sg-modal--library ${mobileView === 'content' ? 'sg-modal--library-mobile-content' : ''}`
      }
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sg-modal__header">
        {selectedEntry && (
          <button
            className="sg-modal__back-btn"
            onClick={handleBack}
            aria-label="Back to library"
          >
            <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
            <span className="sg-modal__back-btn-label">Back</span>
          </button>
        )}
        <div>
          <div className="sg-modal__title">Evidence Library</div>
          {!selectedEntry && (
            <div className="sg-modal__subtitle">Browse all available evidence tables</div>
          )}
        </div>
        <div className="sg-modal__header-actions">
          {showTierToggle && (
            <div className="sg-lens-toggle">
              <button className={lens === 'NON_BLACKI' ? 'active' : ''} onClick={() => setLens('NON_BLACKI')}>Other IIMs</button>
              <button className={lens === 'BLACKI' ? 'active' : ''} onClick={() => setLens('BLACKI')}>Old IIMs (ABCLKI)</button>
            </div>
          )}
          {!fullPage && <button className="sg-modal__close" onClick={onClose}><X size={18} /></button>}
        </div>
      </div>

      <div className="sg-modal__body sg-library-body">
        <div className="sg-library-nav">
          {categories.map((category, catIdx) => (
            <LibraryCategory
              key={catIdx}
              category={category}
              evidenceByTier={evidenceByTier}
              lens={lens}
              selectedEntryKey={selectedEntry?.pairedKey || selectedEntry?.id || null}
              onPick={handlePick}
            />
          ))}
        </div>
        <div className="sg-library-content">{renderContent()}</div>
      </div>
    </div>
  );

  if (fullPage) {
    return content;
  }

  return (
    <div className="sg-modal-backdrop">
      {content}
    </div>
  );
};

// Category accordion item.
// Renders one of three button kinds per Library entry:
//   - paired card  (has `pairedKey`): selects the paired card in the parent;
//                   considered available if EITHER underlying data table exists.
//   - tier-aware   (`tierAware`): selects with tier-aware flag so the modal
//                   header shows the lens toggle.
//   - plain        (default): selects a single canonical table.
// Auto-expands when the category contains the currently-selected entry so the
// left-nav tree is self-orienting as the user clicks around.
const LibraryCategory = ({ category, evidenceByTier, lens, selectedEntryKey, onPick }) => {
  const containsSelected = !!(
    selectedEntryKey && category.tables.some((t) => (t.pairedKey || t.id) === selectedEntryKey)
  );
  const [expanded, setExpanded] = useState(containsSelected);
  useEffect(() => {
    if (containsSelected) setExpanded(true);
  }, [containsSelected]);

  const isTableAvailable = (table) => {
    if (table.pairedKey && PAIRED_TABLES[table.pairedKey]) {
      return PAIRED_TABLES[table.pairedKey].tabs.some((tab) =>
        getEvidenceTable(evidenceByTier, null, tab.tableId)
      );
    }
    return !!getEvidenceTable(evidenceByTier, table.tierAware ? lens : null, table.id);
  };

  return (
    <div className="sg-library-category">
      <button
        className="sg-library-category__header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="sg-library-category__name">{category.name}</span>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {expanded && (
        <div className="sg-library-category__body">
          <div className="sg-library-category__tables">
            {category.tables.map((table) => {
              const exists = isTableAvailable(table);
              const entryKey = table.pairedKey || table.id;
              const selected = selectedEntryKey === entryKey;
              return (
                <button
                  key={table.id}
                  className={[
                    'sg-library-table-btn',
                    !exists ? 'sg-library-table-btn--disabled' : '',
                    selected ? 'sg-library-table-btn--selected' : '',
                  ].filter(Boolean).join(' ')}
                  disabled={!exists}
                  onClick={() => onPick(table)}
                  title={!exists ? 'No data for this test' : table.label}
                >
                  {table.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// IMS Footer Component - Minimal design team requirements
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="sg-footer">
      <div className="sg-footer__inner">
        {/* Navigation Links */}
        <div className="sg-footer__links">
          <a href="https://imsindiahelp.freshdesk.com/" target="_blank" rel="noopener noreferrer">Helpdesk</a>
          <span className="sg-footer__divider">|</span>
          <a href="https://www.imsindia.com/faqs/" target="_blank" rel="noopener noreferrer">FAQs</a>
          <span className="sg-footer__divider">|</span>
          <a href="https://www.imsindia.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span className="sg-footer__divider">|</span>
          <a href="https://www.imsindia.com/terms-of-use/" target="_blank" rel="noopener noreferrer">Terms of Use</a>
          <span className="sg-footer__divider">|</span>
          <a href="https://www.imsindia.com/disclaimer/" target="_blank" rel="noopener noreferrer">Disclaimer</a>
        </div>
        
        {/* Social Icons */}
        <div className="sg-footer__social">
          <span>Follow us on</span>
          <a href="https://facebook.com/imsindia" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
            </svg>
          </a>
          <a href="https://twitter.com/ims_india" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
          <a href="https://www.linkedin.com/company/ims-learning-resources" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a href="https://www.instagram.com/ims.india/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
          </a>
          <a href="https://www.youtube.com/channel/UCjX-3wCGEPW3DzlLR35pB7g" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
        </div>
        
        {/* Copyright */}
        <div className="sg-footer__copyright">
          <p>© {currentYear} IMS Learning Resources Pvt. Ltd. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default function StatGenieReportCardV2({ studentPin, studentName: propStudentName, testId, category, token }) {
  const [resolvedCategory, setResolvedCategory] = useState(category || null);
  const [showCategoryModal, setShowCategoryModal] = useState(!category);
  const [categoryInput, setCategoryInput] = useState('');
  const [modalStep, setModalStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(!!category);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  // Async polling state
  const [jobId, setJobId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [queueInfo, setQueueInfo] = useState(null);
  const pollIntervalRef = useRef(null);
  const loadCalledRef = useRef(false);
  const prevCategoryRef = useRef(category || null);
  const mountedRef = useRef(true);
  const [evidenceModal, setEvidenceModal] = useState(null);
  const [evidenceLibraryOpen, setEvidenceLibraryOpen] = useState(false);
  const [visualEvidenceOpen, setVisualEvidenceOpen] = useState(false);
  const [buildChartOpen, setBuildChartOpen] = useState(false);
  const [studentName, setStudentName] = useState(propStudentName || '');
  const [studentProfile, setStudentProfile] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  
  const [showFloatButtons, setShowFloatButtons] = useState(false);
  const [activeTab, setActiveTab] = useState('report'); // 'report', 'evidence', 'skill', 'explorer'

  // Show floating buttons after 300px scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowFloatButtons(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      // Handle both setInterval (polling) and setTimeout (fallback retry)
      clearInterval(pollIntervalRef.current);
      clearTimeout(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const pollJobStatus = useCallback(async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/jobs/${id}`);
      if (!response.ok) throw new Error(`Poll failed: ${response.status}`);
      const data = await response.json();
      const job = data.job || data; // Handle both wrapped and unwrapped responses

      setGenerationProgress(job.progress || 0);
      setGenerationStatus(job.status);
      setQueueInfo({
        position: job.queuePosition,
        estimatedWaitSeconds: job.estimatedWaitSeconds
      });

      if (job.status === 'COMPLETED') {
        stopPolling();
        // Transform job result into the payload format the report expects
        const result = job.result || {};
        setPayload({
          success: true,
          report: result.report,
          insights_text: result.report ? JSON.stringify(result.report, null, 2) : null,
          stats: result.stats,
          evidence: result.evidence,
          progression: result.progression,
          active_context: result.stats?.progression?.active_context || null,
          current_milestone: result.stats?.progression?.current_milestone || null,
          next_milestone: result.stats?.progression?.next_milestone || null,
          metadata: result.metadata,
          _timings: result.timings,
          targetMode: result.targetMode
        });
        setJobId(null);
      } else if (job.status === 'FAILED' || job.status === 'CANCELLED') {
        stopPolling();
        // Log the raw operator/audit errorMessage for debugging, but show
        // a friendly user-facing message in the UI. Raw messages like
        // "Orphan recovery: QUEUED without SQS confirmation for 191min"
        // are meaningful to operators but alarming/confusing to students.
        const rawErr = job.errorMessage || '';
        if (rawErr) console.warn('[StatGenie] Job failed with operator message:', rawErr);
        let friendly = 'Insight generation failed. Please try again.';
        if (/^Orphan recovery/i.test(rawErr)) {
          friendly = 'Your previous request was interrupted. Please try again.';
        } else if (/^Stale recovery/i.test(rawErr)) {
          friendly = 'Your previous request took too long to complete. Please try again.';
        } else if (/^Final failure after/i.test(rawErr)) {
          friendly = 'We could not generate your insights right now. Please try again in a few minutes.';
        }
        setError(friendly);
        setJobId(null);
      }
      // Otherwise: keep polling (QUEUED, BUILDING_CONTEXT, etc.)
    } catch (err) {
      console.error('Polling error:', err);
      // Don't stop polling on transient errors — keep trying
    }
  }, [stopPolling]);

  const startPolling = useCallback((id, initialProgress = 0, initialStatus = 'QUEUED', initialQueueInfo = null) => {
    // Clear any existing poll first to prevent duplicate intervals
    stopPolling();
    setJobId(id);
    // Use backend-provided progress/status on resume (avoids 0%→70% jump)
    setGenerationProgress(initialProgress);
    setGenerationStatus(initialStatus);
    if (initialQueueInfo) setQueueInfo(initialQueueInfo);
    // Poll immediately once, then every 3 seconds
    pollJobStatus(id);
    const intervalId = setInterval(() => {
      if (mountedRef.current) {
        pollJobStatus(id);
      } else {
        clearInterval(intervalId);
      }
    }, 3000);
    pollIntervalRef.current = intervalId;
  }, [pollJobStatus, stopPolling]);

  const load = useCallback(async () => {
    // Prevent duplicate calls from React StrictMode double-invoke
    if (loadCalledRef.current) return;
    loadCalledRef.current = true;

    setLoading(true);
    setError(null);
    setJobId(null);
    setGenerationStatus(null);
    setGenerationProgress(0);
    setQueueInfo(null);
    stopPolling();
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/generate-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentPin, testId, category: resolvedCategory, token })
      });

      if (!response.ok) throw new Error(`Failed to generate insights: ${response.status}`);
      const data = await response.json();

      if (data.report || data.insights) {
        // Cache hit — render immediately
        setPayload(data);
      } else if (data.status === 'queued' || data.status === 'generating') {
        // Async job created or already in progress — start polling
        // Pass backend-provided progress/status so UI resumes accurately
        // (avoids showing 0% when job is already at 70%)
        const resumeProgress = data.currentProgress || 0;
        const resumeStatus = data.currentProgress > 0 ? 'CALLING_AI' : 'QUEUED';
        const resumeQueueInfo = data.queuePosition
          ? { position: data.queuePosition, estimatedWaitSeconds: data.estimatedWaitSeconds }
          : null;

        if (data.jobId) {
          startPolling(data.jobId, resumeProgress, resumeStatus, resumeQueueInfo);
        } else {
          // Generation lock active but no jobId yet — poll with fallback timer
          // The job record should appear within ~30 seconds
          console.log('[INFO] Generation lock active, no jobId yet. Using fallback polling.');
          setQueueInfo(resumeQueueInfo || {
            position: null,
            estimatedWaitSeconds: data.estimatedWaitSeconds || 30
          });
          setGenerationStatus('QUEUED');
          // Retry the full load after 5 seconds to pick up the jobId
          const retryTimer = setTimeout(() => {
            loadCalledRef.current = false;
            load();
          }, 5000);
          // Store timer for cleanup
          pollIntervalRef.current = retryTimer;
        }
      } else {
        setPayload(data);
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [studentPin, testId, resolvedCategory, token, startPolling, stopPolling]);

  useEffect(() => {
    // Only reset load guard when category actually changes (not on StrictMode remount)
    if (prevCategoryRef.current !== resolvedCategory) {
      loadCalledRef.current = false;
      prevCategoryRef.current = resolvedCategory;
    }
    if (resolvedCategory) load();
  }, [load, resolvedCategory]);

  // Cleanup polling on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  // Category modal handlers
  const handleCategoryNext = () => {
    if (!categoryInput) return;
    setModalStep(2);
  };

  const handleCategoryBack = () => setModalStep(1);

  const handleCategoryConfirm = async () => {
    if (!categoryInput) return;
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/student/${studentPin}/category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ category: categoryInput })
      });
      const data = await response.json();
      if (data.success) {
        setResolvedCategory(categoryInput);
        setShowCategoryModal(false);
        setLoading(true); // trigger load via resolvedCategory change
      } else {
        alert(`Failed to save category: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Error saving category: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const detailedData = payload?.detailedData || payload || {};
  const evidenceByTier = payload?.evidence || detailedData?.evidence || {};
  // Map the API response structure to what buildTopCards expects
  const topCardsData = {
    current_performance: payload?.stats?.current_performance || detailedData?.current_performance,
    cutoff_data: payload?.stats?.cutoff_data || detailedData?.cutoff_data
  };
  const topCards = useMemo(() => buildTopCards(topCardsData), [topCardsData]);

  // Derive all narrative content from payload.report (structured JSON) — NO text parsing
  const structuredReport = payload?.report || null;
  const sectionOrder = ['varc', 'dilr', 'qa'];
  const reportSections = useMemo(() => {
    if (!structuredReport?.sections) return [];
    return sectionOrder
      .filter((key) => structuredReport.sections[key])
      .map((key) => {
        const sec = structuredReport.sections[key];
        return {
          title: key.toUpperCase(),
          key,
          strategyContext: sec.strategy_context || '',
          questionSelection: sec.question_selection || null,
          accuracyAndTime: sec.accuracy_and_time || null,
          conceptTopicCoverage: sec.concept_topic_coverage || null,
          nextStep: sec.next_step || '',
        };
      });
  }, [structuredReport]);
  const historicalPatternByTier = structuredReport?.summary?.historical_pattern || null;
  const currentPositionByTier = structuredReport?.summary?.current_position || null;
  const nextPlan = structuredReport?.next_two_tests_plan || null;

  // Prefer AI-authored goal (report.goal) over frontend-derived fallback.
  // Fallback retained for cache-miss / malformed-AI-response edge cases only.
  const progression = payload?.progression || payload?.stats?.progression;
  const immediateGoal = useMemo(() => {
    const aiGoal = structuredReport?.goal;
    const aiGoalValid = aiGoal && aiGoal.title && aiGoal.why && aiGoal.focus;
    const goal = aiGoalValid ? aiGoal : deriveImmediateGoalFromProgression(progression);
    return goal;
  }, [structuredReport, progression]);

  // ── Category Modal — early return (no page content behind it) ──
  if (showCategoryModal && !resolvedCategory) {
    return (
      <div className="statgenie-v2">
        <div className="sg-category-overlay">
          <div className="sg-category-modal">
            {modalStep === 1 && (
              <>
                <div className="sg-category-modal__header">
                  <div className="sg-category-modal__dots"><span className="sg-category-modal__dot sg-category-modal__dot--active" /><span className="sg-category-modal__dot" /></div>
                  <h2>What's your reservation category?</h2>
                  <p>Your category sets the cutoff benchmarks used across all your insights — IIM readiness, section analysis, and action plans.</p>
                </div>
                <div className="sg-category-modal__body">
                  <select value={categoryInput} onChange={(e) => setCategoryInput(e.target.value)} className="sg-category-modal__select">
                    <option value="" disabled>Choose your category</option>
                    {CATEGORY_OPTIONS.map((cat) => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
                  </select>
                  <div className="sg-category-modal__warning">
                    <AlertTriangle size={16} />
                    <p><strong>Choose carefully.</strong> This is a one-time setting and cannot be changed after confirmation.</p>
                  </div>
                </div>
                <div className="sg-category-modal__footer">
                  <button onClick={handleCategoryNext} disabled={!categoryInput} className="sg-category-modal__btn sg-category-modal__btn--primary">Continue <ChevronRight size={16} /></button>
                </div>
              </>
            )}
            {modalStep === 2 && (
              <>
                <div className="sg-category-modal__header">
                  <div className="sg-category-modal__dots"><span className="sg-category-modal__dot" /><span className="sg-category-modal__dot sg-category-modal__dot--active" /></div>
                  <h2>Confirm your selection</h2>
                  <p>Once confirmed, your category is locked in. All cutoff data and benchmarks will be calibrated to it.</p>
                </div>
                <div className="sg-category-modal__body">
                  <div className="sg-category-modal__selected">
                    <span className="sg-category-modal__selected-label">Selected category</span>
                    <span className="sg-category-modal__selected-value">{CATEGORY_OPTIONS.find(c => c.value === categoryInput)?.label || categoryInput}</span>
                  </div>
                  <div className="sg-category-modal__danger">
                    <p><strong>This cannot be changed later.</strong></p>
                    <p>Your IIM readiness benchmarks, section cutoffs, and all insights will be permanently tied to this category.</p>
                  </div>
                </div>
                <div className="sg-category-modal__footer sg-category-modal__footer--two">
                  <button onClick={handleCategoryBack} disabled={isSaving} className="sg-category-modal__btn sg-category-modal__btn--secondary"><ChevronRight size={16} className="rotate-180" /> Back</button>
                  <button onClick={handleCategoryConfirm} disabled={isSaving} className="sg-category-modal__btn sg-category-modal__btn--primary">{isSaving ? 'Saving...' : 'Confirm category'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="statgenie-v2">
        <div className="sg-loading sg-loading--compact">
          <div className="sg-loading__spinner"></div>
          <p className="sg-loading__text">Generating your personalized insights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="statgenie-v2">
        <div className="sg-loading">
          <AlertTriangle size={44} color="var(--ims-danger)" />
          <p className="sg-loading__text">{error}</p>
          <button className="sg-print-btn" onClick={() => { loadCalledRef.current = false; load(); }}>Try again</button>
        </div>
      </div>
    );
  }

  // Guard: if backend returned 'generating'/'queued' status or no actual insights, show loading
  const hasRealInsights = !!payload?.report || !!payload?.insights;
  const isGenerating = payload?.status === 'generating';
  const isQueued = generationStatus === 'QUEUED' || generationStatus === 'BUILDING_CONTEXT' || generationStatus === 'GENERATING_STATS' || generationStatus === 'BUILDING_EVIDENCE' || generationStatus === 'RENDERING_PROMPT' || generationStatus === 'CALLING_AI' || generationStatus === 'POST_PROCESSING' || generationStatus === 'SAVING';
  const isPolling = !!jobId || isQueued;

  // Helper to format wait time as a human-friendly string
  const formatWaitTime = (seconds) => {
    if (!seconds || seconds <= 0) return 'a moment';
    if (seconds < 60) {
      const secs = Math.max(5, Math.ceil(seconds / 5) * 5); // round up to nearest 5s
      return `about ${secs} seconds`;
    }
    const mins = Math.ceil(seconds / 60);
    return `about ${mins} minute${mins > 1 ? 's' : ''}`;
  };

  // Helper to get queue message — ETA-only, no queue position exposed to user
  const getQueueMessage = () => {
    const eta = queueInfo?.estimatedWaitSeconds;
    const etaMins = eta ? Math.ceil(eta / 60) : 0;

    // If we have real-time progress from the worker, show that
    if (generationProgress > 0 && (!eta || eta <= 30)) {
      return 'Generating your AI insight...';
    }

    // No ETA available — show generic processing message
    if (!eta || eta <= 0) {
      return generationProgress > 0
        ? 'Processing your insight...'
        : 'Generating your AI insight...';
    }

    // Under 1 minute — show seconds-based message
    if (eta < 60) {
      return 'Your AI insight is being prepared — should be ready in a moment.';
    }

    // 1-3 minutes — show time, encourage brief wait
    if (etaMins <= 3) {
      return `Your AI insight is being prepared — should be ready in ${formatWaitTime(eta)}.`;
    }

    // 3+ minutes — show time, reassure they can close the page
    return `Your AI insight is being generated in the background. You can close this page and check back in ${formatWaitTime(eta)}.`;
  };

  if (isGenerating || isPolling || !hasRealInsights) {
    return (
      <div className="statgenie-v2">
        <div className="sg-loading sg-loading--compact">
          <div className="sg-loading__spinner"></div>
          <p className="sg-loading__text">
            {isGenerating || isPolling
              ? getQueueMessage()
              : 'Preparing your personalized insights...'}
          </p>
          {generationProgress > 0 && (
            <div style={{ width: '240px', margin: '12px auto', background: '#e5e7eb', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
              <div style={{ width: `${generationProgress}%`, background: 'var(--ims-primary)', height: '100%', transition: 'width 0.5s ease', borderRadius: '4px' }}></div>
            </div>
          )}
          <p className="sg-loading__subtext">
            {(() => {
              const eta = queueInfo?.estimatedWaitSeconds;
              const etaMins = eta ? Math.ceil(eta / 60) : 0;
              if (generationProgress > 0) {
                return `Step: ${generationStatus?.replace(/_/g, ' ').toLowerCase() || 'processing'}`;
              }
              if (isPolling && etaMins >= 3) {
                return 'Your request is being processed — it\'ll be ready when you return.';
              }
              if (isPolling) {
                return 'You will be automatically redirected when ready.';
              }
              return 'This may take up to 60 seconds. Please wait.';
            })()}
          </p>
        </div>
      </div>
    );
  }

  // Copy restriction — block copy/cut/right-click on report content,
  // but let form fields (textarea/input) pass through so users can still
  // interact with the feedback form normally.
  const isFormField = (target) =>
    target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable);
  const blockIfNotFormField = (e) => { if (!isFormField(e.target)) e.preventDefault(); };

  return (
    <div
      className="statgenie-v2"
      onCopy={blockIfNotFormField}
      onCut={blockIfNotFormField}
      onContextMenu={blockIfNotFormField}
    >
      {/* Top Navigation Bar */}
      <nav className="sg-top-nav">
        <div className="sg-top-nav__logo">
          <img 
            src="https://www.imsindia.com/ims-india/wp-content/themes/ims-india-new/img/icons/logo.svg" 
            alt="IMS India" 
            className="sg-logo"
          />
        </div>
        <div className="sg-top-nav__profile">
          <button 
            className="sg-profile-btn" 
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
          >
            <User size={22} />
            <span className="sg-profile-name-text">{studentName?.split(' ')[0] || 'Profile'}</span>
            <ChevronDown size={14} />
          </button>
          {profileDropdownOpen && (
            <div className="sg-profile-dropdown">
              <div className="sg-profile-info">
                <div className="sg-profile-name">{studentProfile?.name || studentName}</div>
                <div className="sg-profile-detail">PIN: {studentProfile?.studentPin || studentPin}</div>
                <div className="sg-profile-detail">Category: {studentProfile?.category || resolvedCategory}</div>
              </div>
              <div className="sg-profile-divider"></div>
              <button className="sg-profile-action" onClick={() => {
                setProfileDropdownOpen(false);
                document.querySelector('.sg-rating-card')?.scrollIntoView({ behavior: 'smooth' });
              }}>
                <Star size={16} />
                Rate Report
              </button>
              <div className="sg-profile-divider"></div>
            </div>
          )}
        </div>
      </nav>

      {/* Report Header — product title + BETA pill + dynamic test-name tagline.
          Why a block header instead of the old inline subheader strip? The
          spec requires a prominent title with a beta pill and a supporting
          tagline; a single centered line cannot carry both without feeling
          cramped on mobile. */}
      <header className="sg-report-header">
        <h1 className="sg-report-header__title">
          <span className="sg-report-header__title-text">AI Insights Report</span>
          <BetaBadge />
        </h1>
        <p className="sg-report-header__tagline">
          {detailedData?.metadata?.test_name || `Test ${testId}`}
        </p>
      </header>

      {/* Top-Level Tab Navigation Wrapper */}
      <div className={`sg-main-tab-nav-wrapper`}>
        <div className="sg-main-tab-nav">

          <div className="sg-report-tab-anchor">
            <button 
              className={`sg-main-tab ${activeTab === 'report' ? 'sg-main-tab--active' : ''}`}
              onClick={() => setActiveTab('report')}
            >
              AI Insights
            </button>
          </div>

          <button 
            className={`sg-main-tab ${activeTab === 'evidence' ? 'sg-main-tab--active' : ''}`}
            onClick={() => setActiveTab('evidence')}
          >
            Evidence Tables
          </button>

          <button 
            className={`sg-main-tab ${activeTab === 'skill' ? 'sg-main-tab--active' : ''}`}
            onClick={() => setActiveTab('skill')}
          >
            Skill Meter
          </button>

          <button 
            className={`sg-main-tab ${activeTab === 'explorer' ? 'sg-main-tab--active' : ''}`}
            onClick={() => setActiveTab('explorer')}
          >
            Data Explorer
          </button>
        </div>
      </div>

      {/* Report Tab Content */}
      {activeTab === 'report' && (
        <>
          <main className="sg-container">
            <BetaInfoBanner />
            <section className="sg-overall-section">
              {topCards.filter((c) => c.key === 'overall').map((card) => <CompactTopCard key={card.key} card={card} />)}
            </section>

            <section className="sg-sections-row">
              {topCards.filter((c) => c.key !== 'overall').map((card) => <CompactTopCard key={card.key} card={card} />)}
            </section>

            <WhatThisMeansCard currentPosition={currentPositionByTier} historicalPattern={historicalPatternByTier} activeTier={progression?.active_context?.active_tier} topCards={topCards} onOpenEvidence={setEvidenceModal} />
            <ImmediateNextGoalCard goal={immediateGoal} />
            <SectionTabs sections={reportSections} evidenceByTier={evidenceByTier} onOpenEvidence={setEvidenceModal} />
            <NextTwoTestsPlan nextPlan={nextPlan} />
          </main>
        </>
      )}

      {/* Evidence Tables Tab Content */}
      {activeTab === 'evidence' && (
        <div className="sg-tab-content">
          <EvidenceLibraryModal
            evidenceByTier={evidenceByTier}
            onClose={() => setActiveTab('report')}
            fullPage={true}
          />
        </div>
      )}

      {/* Skill Meter Tab Content */}
      {activeTab === 'skill' && (
        <div className="sg-tab-content">
          <SkillMeter stats={payload?.stats} />
        </div>
      )}

      {/* Data Explorer Tab Content */}
      {activeTab === 'explorer' && (
        <div className="sg-tab-content">
          <DataExplorer stats={payload?.stats} />
        </div>
      )}

      {/* ── Common Bottom Section ── */}
      {/* AI Disclaimer — Report tab only */}
      {activeTab === 'report' && (
        <div className="sg-ai-disclaimer">
          <p>
            <strong>AI-Generated Insights:</strong> This report uses AI to analyze your performance data. 
            While we strive for accuracy, AI-generated content may occasionally contain errors or omissions. 
            Please use these insights as guidance alongside your own judgment and mentor feedback.
          </p>
        </div>
      )}
      {/* Rating Card — visible on ALL tabs */}
      <RatingCard studentPin={studentPin} testId={testId} />

      {/* Floating Top Button - Desktop Only */}
      <button 
        className={`sg-top-btn ${showFloatButtons ? 'sg-top-btn--visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
      >
        ↑ Top
      </button>

      {/* Persistent Mobile Bottom Tab Bar — 4-column grid, always visible */}
      <nav className="sg-mobile-tab-bar" aria-label="Main navigation">
        <button
          className={`sg-mobile-tab-bar__btn ${activeTab === 'report' ? 'sg-mobile-tab-bar__btn--active' : ''}`}
          onClick={() => { setActiveTab('report'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <FileText size={16} strokeWidth={2} />
          <span>Insights</span>
        </button>
        <button
          className={`sg-mobile-tab-bar__btn ${activeTab === 'evidence' ? 'sg-mobile-tab-bar__btn--active' : ''}`}
          onClick={() => { setActiveTab('evidence'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <Table2 size={16} strokeWidth={2} />
          <span>Evidence</span>
        </button>
        <button
          className={`sg-mobile-tab-bar__btn ${activeTab === 'skill' ? 'sg-mobile-tab-bar__btn--active' : ''}`}
          onClick={() => { setActiveTab('skill'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <Gauge size={16} strokeWidth={2} />
          <span>Skills</span>
        </button>
        <button
          className={`sg-mobile-tab-bar__btn ${activeTab === 'explorer' ? 'sg-mobile-tab-bar__btn--active' : ''}`}
          onClick={() => { setActiveTab('explorer'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <BarChart3 size={16} strokeWidth={2} />
          <span>Explorer</span>
        </button>
      </nav>

      <EvidenceModal modal={evidenceModal} evidenceByTier={evidenceByTier} onClose={() => setEvidenceModal(null)} />

      {/* IMS Footer — visible on all tabs for consistent page ending */}
      <Footer />
    </div>
  );
}
